import { useState, useMemo } from 'react';
import classNames from 'classnames';
import { MediaPlayer } from '../hooks/useMediaPlayer';
import { PlayMode } from '..';
import { ActorInfo } from '../mini-dialog-types';

import styles from '../mini-dialog.module.css';

export type MiniDialogLineProps = {
  actor: ActorInfo;
  text: string;
  keywordsHidden: boolean;
  audioUrl?: string;
  urlLoaded: string | undefined;
  playMode: PlayMode;
  mediaPlayerStatus: MediaPlayer['status'];
  onPlay?: (playUrl: string) => void | undefined;
  onPause: () => void;
  onRecord?: () => Promise<{ stopRecording: () => Promise<string> } | undefined>;
  translations: any;
  urlPrefix?: string;
};

const parseKeywords = (text: string): string => {
  return text.replace(/\[/g, `<span class="${styles.keyword}">`).replace(/\]/g, '</span>');
};

export function MiniDialogLine({
  text,
  keywordsHidden,
  actor,
  audioUrl,
  onPlay,
  onPause,
  onRecord,
  urlLoaded,
  playMode,
  mediaPlayerStatus,
  urlPrefix,
  translations,
}: MiniDialogLineProps): JSX.Element {
  const formattedText: string = useMemo(() => {
    return parseKeywords(text);
  }, [text]);

  const [recordingSession, setRecordingSession] = useState<{ stop: () => Promise<string> }>();
  const [recordingUrl, setRecordingUrl] = useState<string>();

  const dialogPlaying = playMode === 'DIALOG' && mediaPlayerStatus === 'playing';

  const audioPlaying = urlLoaded ? urlLoaded === audioUrl && mediaPlayerStatus === 'playing' : false;
  const audioPlayDisabled = Boolean(dialogPlaying || !onPlay || !audioUrl || actor.mute);

  const recordingPlaying = urlLoaded ? urlLoaded === recordingUrl && mediaPlayerStatus === 'playing' : false;
  const recordingPlayDisabled = Boolean(dialogPlaying || !onPlay || !recordingUrl);

  const activeLine = urlLoaded
    ? (urlLoaded === audioUrl || urlLoaded === recordingUrl) &&
      (mediaPlayerStatus === 'playing' || mediaPlayerStatus === 'paused')
    : false;

  return (
    <tr
      className={classNames(styles.borderBottom, {
        [styles.active]: activeLine,
      })}
    >
      <td className={classNames(styles.tableCell, styles.avatarWrapper)}>
        <div className={styles.avatar} data-testid="avatar">
          {actor.image && <img src={`${urlPrefix || ''}/api/files/${actor.image}`} alt={actor.name} />}
        </div>
      </td>
      <td className={classNames(styles.tableCell, styles.actor)}>{actor.name}</td>
      <td
        className={classNames(styles.tableCell, {
          [styles.blank]: keywordsHidden,
        })}
      >
        <div
          className={classNames(styles.text, {
            [styles.visuallyHideText]: actor.hideText,
          })}
          dangerouslySetInnerHTML={{ __html: formattedText }}
        ></div>
      </td>
      <td className={classNames(styles.tableCell, styles.actions)}>
        {!dialogPlaying && audioPlaying ? (
          <button
            type="button"
            title={translations.pauseTitle}
            className={classNames(styles.roundedButton, styles.pauseButton)}
            onClick={onPause}
          >
            <i className="bc-icon bc-icon-pause-outline" role="img" />
          </button>
        ) : (
          <button
            type="button"
            title={translations.playTitle}
            disabled={audioPlayDisabled}
            className={classNames(styles.roundedButton, { [styles.disabled]: audioPlayDisabled })}
            onClick={(): void => {
              if (onPlay && audioUrl) {
                onPlay(audioUrl);
              }
            }}
          >
            <i className="bc-icon bc-icon-audio-play" role="img" />
          </button>
        )}
      </td>
      <td className={classNames(styles.tableCell, styles.actions)}>
        {!recordingSession ? (
          <button
            type="button"
            title={translations.recordTitle}
            disabled={Boolean(!onRecord || dialogPlaying)}
            className={classNames(styles.roundedButton, {
              [styles.disabled]: Boolean(!onRecord || dialogPlaying),
            })}
            onClick={async (): Promise<void> => {
              if (onRecord) {
                const record = await onRecord();
                record && setRecordingSession({ stop: record.stopRecording });
              }
            }}
          >
            <i className="bc-icon bc-icon-record" role="img" />
          </button>
        ) : (
          <button
            type="button"
            title={translations.stopRecordTitle}
            className={styles.stopRecordButton}
            onClick={async (): Promise<void> => {
              const url = await recordingSession.stop();
              setRecordingUrl(url);

              setRecordingSession(undefined);
            }}
          >
            <i className="bc-icon bc-icon-audio-stop" role="img" />
          </button>
        )}
        {recordingPlaying ? (
          <button
            type="button"
            title={translations.pauseRecordingTitle}
            className={classNames(styles.roundedButton, styles.pauseButton)}
            onClick={onPause}
          >
            <i className="bc-icon bc-icon-pause-outline" role="img" />
          </button>
        ) : (
          <button
            type="button"
            title={translations.playRecordingTitle}
            disabled={recordingPlayDisabled}
            className={classNames(styles.roundedButton, { [styles.disabled]: recordingPlayDisabled })}
            onClick={(): void => {
              if (onPlay && recordingUrl) {
                onPlay(recordingUrl);
              }
            }}
          >
            <i className="bc-icon bc-icon-audio-play" role="img" />
          </button>
        )}
      </td>
    </tr>
  );
}
